
import { Pencil, Trash2, FileText, ChevronUp, ChevronDown, ChevronsUpDown } from "lucide-react";
import { formatCurrency } from "@/lib/data";
import EmptyState from "../../components/EmptyState";
import { Client } from "../types";
import { Users } from "lucide-react";

export type SortCol = "name" | "company" | "events" | "revenue" | "balance";
export type SortDir = "asc" | "desc";

interface ClientsTableProps {
  clients: Client[];
  isFiltered: boolean;
  isStaff: boolean;
  sortCol: SortCol | null;
  sortDir: SortDir;
  onSort: (col: SortCol) => void;
  onEdit: (client: Client) => void;
  onDelete: (client: Client) => void;
  onLedger: (client: Client) => void;
  onClearFilter: () => void;
  onAddClient: () => void;
}

function SortIcon({ col, sortCol, sortDir }: { col: SortCol; sortCol: SortCol | null; sortDir: SortDir }) {
  if (sortCol !== col) return <ChevronsUpDown className="w-3 h-3 opacity-30 ml-0.5" />;
  return sortDir === "asc"
    ? <ChevronUp className="w-3 h-3 text-amber-600 ml-0.5" />
    : <ChevronDown className="w-3 h-3 text-amber-600 ml-0.5" />;
}

function Th({
  col, label, align = "left", sortCol, sortDir, onSort,
}: {
  col: SortCol; label: string; align?: "left" | "right";
  sortCol: SortCol | null; sortDir: SortDir; onSort: (c: SortCol) => void;
}) {
  return (
    <th
      onClick={() => onSort(col)}
      className={`
        px-5 py-3.5 text-xs font-semibold text-stone-500 uppercase tracking-wider cursor-pointer
        select-none hover:text-stone-700 hover:bg-stone-100/80 transition-colors
        ${align === "right" ? "text-right" : "text-left"}
      `}
    >
      <span className={`inline-flex items-center gap-0 ${align === "right" ? "flex-row-reverse" : ""}`}>
        {label}
        <SortIcon col={col} sortCol={sortCol} sortDir={sortDir} />
      </span>
    </th>
  );
}

export default function ClientsTable({
  clients,
  isFiltered,
  isStaff,
  sortCol,
  sortDir,
  onSort,
  onEdit,
  onDelete,
  onLedger,
  onClearFilter,
  onAddClient,
}: ClientsTableProps) {
  if (clients.length === 0) {
    return (
      <EmptyState
        icon={Users}
        title="No clients found"
        description={isFiltered ? "No clients match the current filters." : "Add your first client to start tracking events and payments."}
        isFiltered={isFiltered}
        onClearFilter={onClearFilter}
        action={{ label: "Add Client", onClick: onAddClient }}
      />
    );
  }

  const totalBalance = clients.reduce((s, c) => s + (Number(c.balance) || 0), 0);

  return (
    <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden shadow-sm">
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-stone-50 border-b border-stone-100">
              <Th col="name" label="Client" sortCol={sortCol} sortDir={sortDir} onSort={onSort} />
              <th className="text-left px-5 py-3.5 text-xs font-semibold text-stone-500 uppercase tracking-wider">
                Contact
              </th>
              <Th col="events" label="Events" align="right" sortCol={sortCol} sortDir={sortDir} onSort={onSort} />
              <Th col="revenue" label="Billed" align="right" sortCol={sortCol} sortDir={sortDir} onSort={onSort} />
              <Th col="balance" label="Balance" align="right" sortCol={sortCol} sortDir={sortDir} onSort={onSort} />
              <th className="px-5 py-3.5 w-28" />
            </tr>
          </thead>

          <tbody className="divide-y divide-stone-100">
            {clients.map(client => (
              <tr key={client.id} className="hover:bg-stone-50/60 transition-colors group">
                {/* Client identity */}
                <td className="px-5 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-amber-50 flex items-center justify-center text-sm font-bold text-amber-600 flex-shrink-0 border border-amber-100">
                      {client.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-stone-800 truncate">{client.name}</p>
                      {client.company_name && (
                        <p className="text-xs text-stone-400 truncate">{client.company_name}</p>
                      )}
                    </div>
                  </div>
                </td>

                {/* Contact */}
                <td className="px-5 py-4">
                  <div className="text-sm space-y-0.5">
                    {client.phone && <div className="text-stone-600">{client.phone}</div>}
                    {client.email && <div className="text-xs text-stone-400">{client.email}</div>}
                    {!client.phone && !client.email && <span className="text-stone-300 text-xs">—</span>}
                  </div>
                </td>

                {/* Events */}
                <td className="px-5 py-4 text-right">
                  {(client.events_count || 0) > 0 ? (
                    <span className="inline-flex items-center justify-center min-w-[28px] h-6 px-2 rounded-full bg-violet-50 text-violet-700 text-xs font-bold border border-violet-100">
                      {client.events_count}
                    </span>
                  ) : (
                    <span className="text-stone-300 text-sm">0</span>
                  )}
                </td>

                {/* Revenue */}
                <td className="px-5 py-4 text-right">
                  <span className="text-sm font-semibold text-stone-700 tabular-nums">
                    {formatCurrency(client.total_revenue || 0)}
                  </span>
                </td>

                {/* Balance */}
                <td className="px-5 py-4 text-right">
                  {(client.balance || 0) > 0 ? (
                    <span className="inline-block px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 text-xs font-bold border border-amber-200 tabular-nums">
                      {formatCurrency(client.balance || 0)}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs text-emerald-600 font-semibold">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" />
                      Cleared
                    </span>
                  )}
                </td>

                {/* Actions */}
                <td className="px-5 py-4">
                  {/* Hover-revealed on mouse devices; always visible on touch screens / keyboard focus. */}
                  <div className="flex justify-end items-center gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 [@media(hover:none)]:opacity-100 transition-opacity">
                    <button
                      onClick={() => onLedger(client)}
                      className="p-1.5 rounded-lg text-amber-600 hover:bg-amber-50 transition-colors"
                      title="View Ledger"
                    >
                      <FileText className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => onEdit(client)}
                      className="p-1.5 rounded-lg text-stone-500 hover:bg-stone-100 transition-colors"
                      title="Edit Client"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    {!isStaff && (
                      <button
                        onClick={() => onDelete(client)}
                        className="p-1.5 rounded-lg text-red-400 hover:bg-red-50 transition-colors"
                        title="Delete Client"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>

          {/* Totals footer row */}
          {clients.length > 1 && (
            <tfoot>
              <tr className="bg-stone-50 border-t-2 border-stone-200">
                <td className="px-5 py-3 text-xs font-bold text-stone-600" colSpan={2}>
                  {clients.length} clients
                </td>
                <td className="px-5 py-3 text-right text-xs font-bold text-stone-700">
                  {clients.reduce((s, c) => s + (c.events_count || 0), 0)}
                </td>
                <td className="px-5 py-3 text-right text-xs font-bold text-stone-700 tabular-nums">
                  {formatCurrency(clients.reduce((s, c) => s + (Number(c.total_revenue) || 0), 0))}
                </td>
                <td className="px-5 py-3 text-right">
                  {totalBalance > 0
                    ? <span className="text-xs font-bold text-amber-700 tabular-nums">{formatCurrency(totalBalance)}</span>
                    : <span className="text-xs font-bold text-emerald-600">All Cleared</span>}
                </td>
                <td />
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {/* Mobile cards (< md) */}
      <div className="md:hidden divide-y divide-stone-100">
        {clients.map(client => {
          const hasBalance = (client.balance || 0) > 0;
          return (
            <div key={client.id} className="p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-xl bg-amber-50 flex items-center justify-center text-sm font-bold text-amber-600 flex-shrink-0 border border-amber-100">
                    {client.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold text-stone-800 truncate">{client.name}</p>
                    {client.company_name && (
                      <p className="text-xs text-stone-500 mt-0.5 truncate">{client.company_name}</p>
                    )}
                  </div>
                </div>
                {hasBalance ? (
                  <span className="flex-shrink-0 inline-block px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 text-xs font-bold border border-amber-200">
                    Due
                  </span>
                ) : (
                  <span className="flex-shrink-0 inline-flex items-center gap-1 text-xs text-emerald-600 font-semibold">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" />
                    Cleared
                  </span>
                )}
              </div>
              <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                <div className="min-w-0">
                  <p className="text-xs text-stone-400">Billed</p>
                  <p className="font-semibold text-stone-700 tabular-nums truncate">{formatCurrency(client.total_revenue || 0)}</p>
                </div>
                <div className="min-w-0 text-right">
                  <p className="text-xs text-stone-400">Balance</p>
                  <p className={`font-bold tabular-nums truncate ${hasBalance ? "text-amber-700" : "text-emerald-600"}`}>
                    {formatCurrency(client.balance || 0)}
                  </p>
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-stone-400">Events</p>
                  <p className="font-medium text-stone-700">{client.events_count || 0}</p>
                </div>
                <div className="min-w-0 text-right">
                  <p className="text-xs text-stone-400">Contact</p>
                  {client.phone && <p className="text-stone-600 truncate">{client.phone}</p>}
                  {client.email && <p className="text-xs text-stone-400 truncate">{client.email}</p>}
                  {!client.phone && !client.email && <p className="text-stone-300">—</p>}
                </div>
              </div>
              <div className="mt-3 flex flex-wrap items-center justify-end gap-2">
                <button
                  onClick={() => onLedger(client)}
                  className="h-10 px-3 inline-flex items-center justify-center gap-1.5 rounded-lg border border-amber-200 text-amber-600 text-xs font-semibold hover:bg-amber-50 transition-colors"
                  aria-label={`View ledger for ${client.name}`}
                >
                  <FileText className="w-4 h-4" />
                  Ledger
                </button>
                <button
                  onClick={() => onEdit(client)}
                  className="h-10 min-w-10 inline-flex items-center justify-center rounded-lg border border-stone-200 text-stone-500 hover:bg-stone-100 transition-colors"
                  title="Edit Client"
                  aria-label={`Edit ${client.name}`}
                >
                  <Pencil className="w-4 h-4" />
                </button>
                {!isStaff && (
                  <button
                    onClick={() => onDelete(client)}
                    className="h-10 min-w-10 inline-flex items-center justify-center rounded-lg border border-red-200 text-red-400 hover:bg-red-50 transition-colors"
                    title="Delete Client"
                    aria-label={`Delete ${client.name}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          );
        })}

        {/* Totals (mirrors the desktop footer row) */}
        {clients.length > 1 && (
          <div className="px-4 py-3 bg-stone-50 flex flex-wrap items-center justify-between gap-x-4 gap-y-1 text-xs font-bold text-stone-600">
            <span>{clients.length} clients · {clients.reduce((s, c) => s + (c.events_count || 0), 0)} events</span>
            <span className="tabular-nums">
              {formatCurrency(clients.reduce((s, c) => s + (Number(c.total_revenue) || 0), 0))}
              <span className="text-stone-300 mx-1.5">·</span>
              {totalBalance > 0
                ? <span className="text-amber-700">{formatCurrency(totalBalance)} due</span>
                : <span className="text-emerald-600">All Cleared</span>}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
